/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {InnerVerticalTabs} from "../../../../../components/Tabs/InnerVerticalTabs/InnerVerticalTabs";
import Content from "./Content";
import {BooleanParam, StringParam, useQueryParam, withDefault} from "use-query-params";
import {ArgosFavoriteIconLogic} from "../../../../../components/Buttons/ArgosFavoriteIconLogic";
import {FAVORITE_PARAM_ID, FAVORITE_TYPE, INNERVTAB_URL_PARAM, MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME} from "../constants";
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import {Radar} from "../../constants";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import {findObjectByVariantId} from "./CollapsibleTabs/ObjectProperties";


export const ViewPublicConfigsParam = withDefault(BooleanParam, false)

interface RadarPerformanceProps {
  sensor: Radar
  objects: any[]
  gmid: string
  onSaveConfiguration: any
  onDeleteConfiguration: any
}

const RadarPerformance = (props: RadarPerformanceProps) => {
  const [tabs, setTabs] = useState<any>([])
  const [viewPublicConfigurations, setViewPublicConfigurations] = useQueryParam('viewPublicConfigs', ViewPublicConfigsParam)
  const [_index, fff] = useQueryParam(FAVORITE_PARAM_ID, StringParam)

  const groupBy = (array: any, key: any) =>
    array?.reduce((objectsByKeyValue: any, obj: any) => {
      const value = obj[key];
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});

  useEffect(() => {
    const objectData = props.objects
    const sensorRadarData = props.sensor
    const sensorRadarConfigs = sensorRadarData.configurations
    const baselineFromDatabase = sensorRadarData.baseline[0]

    const tempLocalObjectDataMap = Object.entries(groupBy(objectData, 'id')).map<any>(([key, value]: [any, any]) => {
      return {
        'type': value[0].type,
        'name': value[0].name,
        'id': value[0].id,  // TODO: If we want to implement versions, we can group by type and name
        'sub': value.map((elem: any) => ({...elem}))
      }
    })
    const localObjectDataMap = Object.entries(groupBy(tempLocalObjectDataMap, 'type')).map(([key, value]: [any, any]) => {
      return {'type': key, 'sub': value.map((elem: any) => elem.sub)};
    })

    // this is baselineObject constructed from database 
    const baselineObject = {
      "type": "Baseline",

      "name": baselineFromDatabase.objectName,
      "objectVariant": baselineFromDatabase.objectVariant,
      "version": baselineFromDatabase.objectVersion,

      "rcs": baselineFromDatabase.rcs,

      "pixelsAcrossShortDimension": "",
      "pointsOnObject": "",
    }

    localObjectDataMap.push({'type': 'Custom', 'sub': [{}]})
    localObjectDataMap.unshift({'type': 'Baseline', 'sub': [[baselineObject]]})
    objectData.push(baselineObject)

    const detectionRange1 = sensorRadarData.plotData?.['Detection_Range_1']
    const defaultTemperatureValue = detectionRange1?.data?.temperature_1_value

    const defaultSnrValue = detectionRange1?.data?.snr_1_value

    const defaultRcsValue = detectionRange1?.data?.rcs_1_value

    const defaultAttenuationValue = baselineFromDatabase.attenuation ?? 0

    const baselineData = {
      "objectCategory": baselineObject.type,
      "objectName": baselineObject.name,
      "objectVersion": baselineObject.version,
      "objectVariant": baselineObject.objectVariant,

      'temperature': defaultTemperatureValue,
      'snr': defaultSnrValue,
      'rcs': defaultRcsValue,
      'attenuation': defaultAttenuationValue,

      'isPublicConfig': true
    }

    let localTabs: any[] = []

    // Baseline configuration
    const baselineTab = {
      "trackedItemUid": 'baseline',
      "name": "Baseline",
      "content": <Content configIndex={0} baselineData={baselineData} objectData={objectData}
                          objectDataMap={localObjectDataMap}
                          sensorRadarData={sensorRadarData} onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    }
    localTabs.push(baselineTab)

    // Owner configurations
    const ownerConfigTabs = sensorRadarConfigs?.filter((x: object) => x["owner" as keyof object] == props.gmid)
    const configTabs = ownerConfigTabs?.map((singleConfig: any, index: number) => {
        updateSingleConfig(singleConfig, objectData)
        return (
          {
            "trackedItemUid": singleConfig['trackedItemUid'] ?? `item-${index}-${singleConfig['configName']}`,
            "name": singleConfig['configName'],
            "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                                objectDataMap={localObjectDataMap} sensorRadarData={sensorRadarData}
                                onSaveConfiguration={props.onSaveConfiguration}
                                onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
          }
        )
      }
    )
    if (configTabs != null) {
      localTabs = localTabs.concat(configTabs)
    }

    // Public configurations
    if (viewPublicConfigurations) {
      const publicConfigs = sensorRadarConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid)
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => {
        updateSingleConfig(singleConfig, objectData)
        return (
          {
            "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
            "name": singleConfig['configName'],
            "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                                objectDataMap={localObjectDataMap} sensorRadarData={sensorRadarData}
                                onSaveConfiguration={props.onSaveConfiguration}
                                onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
          }
        )
      })
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs)
      }
    }

    setTabs(localTabs)
  }, [props.sensor, props.objects, viewPublicConfigurations, props.gmid, props.onDeleteConfiguration, props.onSaveConfiguration])

  return (
    <div style={{flexGrow: 1, display: 'flex', height: '100%', flexDirection: "column"}}>
      <div style={{display: 'flex', width: '100%'}}>
        <ArgosFlexbox style={{whiteSpace: "pre-wrap", width: "200px",}}>
          <FormControlLabel
            style={{fontSize: "0.8px"}}
            control={<Checkbox
              onChange={(e) => {
                setViewPublicConfigurations(e.target.checked);
              }}
              checked={viewPublicConfigurations} name="viewPublicConfigurations"

            />} label={<Typography fontSize={"0.8rem"}>View Public Configurations</Typography>}/>

        </ArgosFlexbox>
        <div style={{marginLeft: 'auto'}}>
          <ArgosFavoriteIconLogic favoriteType={FAVORITE_TYPE} trackedItemUid={props?.sensor?.id} partitionKey={props?.sensor?.id}/>
        </div>
      </div>
      <InnerVerticalTabs tabs={tabs} favoriteType={FAVORITE_TYPE} uid={INNERVTAB_URL_PARAM}
        // onTabSelected={(index: number) => setSelectedTab(index)}
      />
    </div>
  )
}
export default RadarPerformance;

const updateSingleConfig = (singleConfig: any, objectData: any) => {
  const isBaseline = !('objectId' in singleConfig)
  if (!isBaseline) {
    const objectId = singleConfig['objectId']
    const variantData = findObjectByVariantId(objectData, objectId)
    const variantObj = variantData?.object

    const objectCategory = variantObj?.type
    const objectName = variantObj?.objectName
    const objectVersion = variantObj?.latestVersion
    const profileNameKey = variantData?.variantName
    const objectVariant = MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME[profileNameKey ?? '']

    singleConfig['objectCategory'] = objectCategory
    singleConfig['objectName'] = objectName
    singleConfig['objectVersion'] = objectVersion
    singleConfig['objectVariant'] = objectVariant

    const rcs = variantObj?.parameters?.[profileNameKey ?? '']?.radarCrossSection ?? ''
    singleConfig['rcs'] = rcs
  }
}